import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { Input, InputProps } from "./base";

export interface ControlledInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "onBlur" | "error" | "required" | "inputRef"
> {
  /** Field name registered on the surrounding react-hook-form context. */
  name: string;
  /** Read the error from a different field than `name` (e.g. a grouped error). */
  errorName?: string;
  rules?: {
    required?: { value: boolean; message: string };
    minLength?: { value: number; message: string };
    maxLength?: { value: number; message: string };
    min?: { value: number; message: string };
    max?: { value: number; message: string };
    validate?: (val: string) => string | boolean | undefined;
    pattern?: { value: RegExp; message: string };
  };
}

/**
 * react-hook-form–bound {@link Input}: the form owns the value and validation
 * via `name`/`rules`. Reach for this inside a form; use the plain controlled
 * `Input` when a non-form parent owns the value, or `UncontrolledInput` when
 * the field is self-contained.
 */
export function ControlledInput({
  name,
  errorName = "",
  rules = {},
  disabled = false,
  ...rest
}: ControlledInputProps) {
  const { control, getFieldState } = useFormContext();
  const { field, fieldState } = useController({ name, control, rules });
  const required = rules.required?.value === true;

  const { error } = useMemo(
    () => getFieldState(errorName ? errorName : name),
    [getFieldState, errorName, name, fieldState.error],
  );

  return (
    <Input
      {...rest}
      name={field.name}
      value={field.value ?? ""}
      onChange={field.onChange}
      onBlur={field.onBlur}
      inputRef={field.ref}
      disabled={field.disabled || disabled}
      required={required}
      error={error?.message}
    />
  );
}

export default ControlledInput;
